import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import style from "./Cart.module.css";
import useCartStore from "../../states/CartStore";
import { updateCartItemQuantity, removeCartItem, clearCartItem } from "../../services/OnlineStore";
import Page from "../../components/page/Page"
import CartProduct from "../../components/cartProduct/CartProduct"
import SummaryContainer from "../../components/SummaryContainer/SummaryContainer"
import { LuShoppingCart } from "react-icons/lu";
import { FaCreditCard } from "react-icons/fa";

function getItemPriceCents(item) {
  return item.product_variant?.price_cents ?? item.product_variant?.effective_price_cents ?? 0;
}

function Cart() {
  const navigate = useNavigate();
  const cart = useCartStore((state) => state.cart);
  const setCart = useCartStore((state) => state.setCart);
  const [isMutating, setIsMutating] = useState(false);
  const [ isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const findCartItem = (itemId) => cart?.items?.find((item) => item.id === itemId);

  const applyOptimisticQuantity = (currentCart, itemId, newQuantity) => {
    const targetItem = currentCart.items.find((item) => item.id === itemId);

    if (!targetItem) {
      return currentCart;
    }

    const oldQuantity = targetItem.quantity;
    const deltaQuantity = newQuantity - oldQuantity;
    const unitPrice = getItemPriceCents(targetItem);

    return {
      ...currentCart,
      subtotal_cents: (currentCart.subtotal_cents || 0) + (deltaQuantity * unitPrice),
      items: currentCart.items.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      ),
    };
  };

  const applyOptimisticRemove = (currentCart, itemId) => {
    const targetItem = currentCart.items.find((item) => item.id === itemId);

    if (!targetItem) {
      return currentCart;
    }

    const deduction = targetItem.quantity * getItemPriceCents(targetItem);

    return {
      ...currentCart,
      subtotal_cents: Math.max(0, (currentCart.subtotal_cents || 0) - deduction),
      items: currentCart.items.filter((item) => item.id !== itemId),
    };
  };

  const handleRemoveItem = async (itemId) => {
    if (!cart?.cart_token || !cart?.items || isMutating) return;

    const item = findCartItem(itemId);
    if (!item?.product_variant?.id) return;

    const previousCart = cart;
    const optimisticCart = applyOptimisticRemove(cart, itemId);

    setCart(optimisticCart);
    setIsMutating(true);

    try {
      const serverCart = await removeCartItem(cart.cart_token, item.product_variant.id);
      setCart(serverCart);
    } catch (error) {
      console.error("Failed to remove item from cart:", error);
      setCart(previousCart);
    } finally {
      setIsMutating(false);
    }
  };

  const handleClearItem = async (itemId) => {
    if (!cart?.cart_token || !cart?.items || isMutating) return;

    const item = findCartItem(itemId);
    if (!item?.product_variant?.id) return;

    const previousCart = cart;
    const optimisticCart = applyOptimisticRemove(cart, itemId);

    setCart(optimisticCart);
    setIsMutating(true);

    try {
      await clearCartItem(
        cart.cart_token,
        item.product_variant.id
      );
      setCart(optimisticCart);
    } catch (error) {
      console.error("Failed to clear item from cart:", error);
      setCart(previousCart);
    } finally {
      setIsMutating(false);
    }
  };

  const handleIncreaseQuantity = async (itemId) => {
    if (!cart?.cart_token || !cart?.items || isMutating) return;

    const item = findCartItem(itemId);
    if (!item?.product_variant?.id) return;

    const newQuantity = item.quantity + 1;
    const previousCart = cart;
    const optimisticCart = applyOptimisticQuantity(cart, itemId, newQuantity);

    setCart(optimisticCart);
    setIsMutating(true);

    try {
      const serverCart = await updateCartItemQuantity(
        cart.cart_token,
        item.product_variant.id,
        newQuantity
      );
      setCart(serverCart);
    } catch (error) {
      console.error("Failed to increase item quantity:", error);
      setCart(previousCart);
    } finally {
      setIsMutating(false);
    }
  };
  
  const handleDecreaseQuantity = async (itemId) => {
    if (!cart?.cart_token || !cart?.items || isMutating) return;

    const item = findCartItem(itemId);
    if (!item?.product_variant?.id) return;

    if (item.quantity <= 1) {
      await handleRemoveItem(itemId);
      return;
    }

    const newQuantity = item.quantity - 1;
    const previousCart = cart;
    const optimisticCart = applyOptimisticQuantity(cart, itemId, newQuantity);

    setCart(optimisticCart);
    setIsMutating(true);

    try {
      const serverCart = await updateCartItemQuantity(
        cart.cart_token,
        item.product_variant.id,
        newQuantity
      );
      setCart(serverCart);
    } catch (error) {
      console.error("Failed to decrease item quantity:", error);
      setCart(previousCart);
    } finally {
      setIsMutating(false);
    }
  };

  
  return (
    <>
      {isMobile ? (
        <Page
          skinny={true}
          title="My Cart"
          pageStyles={{
              title: {
                color: "var(--fg-muted)",
                textAlign: "center",
                width: "100%",
              }
          }}
        >   
          <SummaryContainer
                title="Your Total"
                actionButtonTitle="Proceed to Checkout"
                actionButtonIcon={<FaCreditCard size={20} />}
                onAction={() => navigate("/checkout")}
                disabled={cart?.items?.length === 0}
                cartSummaryClassesOverride={{
                  container: style.mobileSummary,
                  actionButton: style.mobileCheckoutButton,
                }}
              >
                  <div className={style.summaryRow}>
                    <p className={style.subtotalLabel}>Subtotal</p>
                    <p className={style.subtotal}>${((cart?.subtotal_cents || 0) / 100).toFixed(2)}</p>
                  </div>
          </SummaryContainer>
            <div className={style.content}>
              {cart?.items?.length > 0 ? (
                <div className={style.items}>
                  {cart?.items?.map((item) => (
                    <CartProduct 
                      key={item.id}
                      item = {item}
                      handleDecreaseQuantity={() => handleDecreaseQuantity(item.id)}
                      handleIncreaseQuantity={() => handleIncreaseQuantity(item.id)}
                      handleRemoveItem={() => handleRemoveItem(item.id)}
                      handleClearItem={() => handleClearItem(item.id)}
                      onClick={() => { navigate(`/products/${item.product_variant.product.id}`); }}
                      // cartProductStyles={cartProductStyles}

                    >
                      
                    </CartProduct>
                  ))}
                </div>
              ) : (
                <div className={style.emptyCart}>
                  <p>Your cart is empty!</p>
                  <button 
                    onClick={() => navigate("/onlineStore")}
                    className={style.continueShoppingButton}
                  >
                    Continue Shopping
                  </button>
                </div>
              )}
            </div>
        </Page> 
      ) : (
        <Page
          skinny={true}
          title="My Cart"
          pageStyles={{
              title: { color: "var(--fg-muted)" }
          }}
        >
            <div className={style.content}>
              {cart?.items?.length > 0 ? (
                <div className={style.items}>
                  {cart?.items?.map((item) => (
                    <CartProduct 
                      key={item.id}
                      item = {item}
                      handleDecreaseQuantity={() => handleDecreaseQuantity(item.id)}
                      handleIncreaseQuantity={() => handleIncreaseQuantity(item.id)}
                      handleRemoveItem={() => handleRemoveItem(item.id)}
                      handleClearItem={() => handleClearItem(item.id)}
                      onClick={() => { navigate(`/products/${item.product_variant.product.id}`); }}
                      // cartProductStyles={cartProductStyles}

                    >
                      
                    </CartProduct>
                  ))}
                </div>
              ) : (
                <div className={style.emptyCart}>
                  <p>Your cart is empty!</p>
                  <button 
                    onClick={() => navigate("/onlineStore")}
                    className={style.continueShoppingButton}
                  >
                    Continue Shopping
                  </button>
                </div>
              )}

              <SummaryContainer
                title="Your Total"
                actionButtonTitle="Proceed to Checkout"
                actionButtonIcon={<FaCreditCard size={20} />}
                onAction={() => navigate("/checkout")}
                disabled={cart?.items?.length === 0}
              >
                  <div className={style.summaryRow}>
                    <p className={style.subtotalLabel}>Subtotal</p>
                    <p className={style.subtotal}>${((cart?.subtotal_cents || 0) / 100).toFixed(2)}</p>
                  </div>
              </SummaryContainer>
            </div>
        </Page> 
      )}
    </>

  );
}

export default Cart;